import React, { useEffect, useState, useRef } from "react";
import SiteNav from "@/components/SiteNav";
import OptimizedImage from "@/components/OptimizedImage";
import SiteFooter from "@/components/SiteFooter";
import { Link } from "wouter";
import { useQuote } from "@/components/QuoteProvider";
import { servicesData } from "@/data/services";
import { Package, Globe, Tag, Clock, ArrowRight, Star, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

const FadeInSection = ({ children, delay = 0, className = "" }: { children: React.ReactNode, delay?: number, className?: string }) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setVisible(true);
        if (domRef.current) observer.unobserve(domRef.current);
      }
    }, { threshold: 0.1, rootMargin: "0px 0px -50px 0px" });
    
    if (domRef.current) observer.observe(domRef.current);
    
    return () => {
      if (domRef.current) observer.unobserve(domRef.current);
    };
  }, []);

  return (
    <div ref={domRef} className={`transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const categoryColors = [
  "bg-blue-50", "bg-slate-50", "bg-indigo-50", "bg-sky-50",
  "bg-violet-50", "bg-cyan-50", "bg-blue-50", "bg-slate-50"
];

export default function Home() {
  const { openQuote } = useQuote();

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace("#", "");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, []);

  return (
    <div className="flex flex-col min-h-[100dvh] font-sans bg-background text-foreground">
      <SiteNav />
      <main className="contents">

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-[#0A0F1C]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-900/20 via-[#0A0F1C] to-[#0A0F1C]"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeInSection>
              <p className="text-blue-500 font-bold tracking-widest text-xs mb-5 uppercase">Premium Procurement</p>
              <h1 className="text-5xl lg:text-7xl font-extrabold text-white mb-6 leading-[1.05] tracking-tight">
                Print. Market.<br/>Win.
              </h1>
              <p className="text-slate-400 text-lg mb-8 max-w-xl leading-relaxed">
                Discover innovative print and marketing solutions built for performance and scale. We source, manage, and deliver premium collateral so you can focus on growing your business.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button onClick={() => openQuote()} size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 h-12 rounded-full font-semibold transition-all shadow-lg shadow-blue-600/30 border-0">
                  Explore Now <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </FadeInSection>
            <FadeInSection delay={200} className="relative">
              <div className="absolute inset-0 bg-blue-500/20 blur-[100px] rounded-full"></div>
              <OptimizedImage src="/hero.jpg" width={1024} height={1024} loading="eager" fetchPriority="high" alt="Premium Print Marketing Solutions" className="relative z-10 w-full object-contain drop-shadow-2xl" onError={(e) => { e.currentTarget.src = "/wide-format-printer.png"; }} />
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
            {[
              { icon: Package, title: "Heavy Lifting", desc: "We manage everything" },
              { icon: Globe, title: "Supplier Network", desc: "200+ trusted partners" },
              { icon: Tag, title: "Cost Efficiency", desc: "Better trade pricing" },
              { icon: Clock, title: "Fast Turnaround", desc: "Reliable delivery" }
            ].map((item, i) => (
              <FadeInSection key={i} delay={i * 100} className="flex items-center gap-4">
                <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-full bg-slate-50 text-blue-600 border border-slate-100">
                  <item.icon className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-slate-900 leading-tight">{item.title}</h2>
                  <p className="text-xs text-slate-500 mt-0.5">{item.desc}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection>
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Our Services</h2>
          </FadeInSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {servicesData.map((service, idx) => (
              <FadeInSection key={service.slug} delay={idx * 50} className="h-full">
                <div className={`${categoryColors[idx % categoryColors.length]} rounded-2xl p-6 relative overflow-hidden group hover:shadow-md transition-all h-48 border border-transparent hover:border-blue-100`}>
                  <div className="relative z-10">
                    <h3 className="font-bold text-lg text-slate-900 mb-1 max-w-[140px] leading-tight">{service.title}</h3>
                    <Link href={`/services/${service.slug}/`} className="text-xs font-semibold text-slate-600 hover:text-blue-600 flex items-center gap-1 group-hover:gap-2 transition-all mt-2 inline-flex">
                      Shop Now <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                  <OptimizedImage src={service.img} alt="" className="absolute -bottom-4 -right-4 w-32 h-32 object-cover rounded-xl shadow-lg opacity-90 group-hover:scale-110 group-hover:opacity-100 transition-all duration-500" />
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Top Picks */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-slate-900">Featured Solutions</h2>
            <Link href="/catalogue/" className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeInSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {servicesData.slice(0, 4).map((service, idx) => (
              <FadeInSection key={service.slug} delay={idx * 100} className="h-full">
                <div className="bg-white border border-slate-100 rounded-2xl p-6 hover:shadow-xl hover:border-slate-200 transition-all group flex flex-col h-full relative">
                  <div className="mb-4">
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${idx === 0 ? 'bg-blue-600 text-white' : idx === 1 ? 'bg-emerald-500 text-white' : idx === 2 ? 'bg-rose-500 text-white' : 'bg-slate-800 text-white'}`}>
                      {idx === 0 ? 'NEW' : idx === 1 ? 'BESTSELLER' : idx === 2 ? 'SALE' : 'POPULAR'}
                    </span>
                  </div>
                  <div className="flex-1 flex flex-col items-center text-center">
                    <div className="w-32 h-32 mb-6 relative">
                      <OptimizedImage src={service.img} alt="" className="w-full h-full object-cover rounded-xl shadow-sm group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">{service.title}</h3>
                    <p className="text-xs text-slate-500 line-clamp-2 mb-6">{service.shortDesc}</p>
                  </div>
                  <Button onClick={() => openQuote([service.title])} className="w-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2 rounded-xl h-11 transition-colors border-0">
                    <Package className="w-4 h-4" /> Request Quote
                  </Button>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Dark Promo Band */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection>
            <div className="bg-[#0A0F1C] rounded-3xl flex flex-col md:flex-row overflow-hidden relative border border-slate-800 shadow-2xl">
              <div className="p-10 md:p-16 flex-1 text-white relative z-10 flex flex-col justify-center">
                <p className="text-blue-500 font-bold text-xs tracking-widest mb-3 uppercase">Agency Advantage</p>
                <h2 className="text-3xl md:text-4xl font-bold mb-4 font-display tracking-tight">Simplify Your Supply Chain</h2>
                <p className="text-slate-400 mb-8 max-w-md text-sm leading-relaxed">
                  Centralise your print and marketing procurement. One point of contact, 200+ trusted suppliers, saving you time and money.
                </p>
                <div>
                  <Button onClick={() => document.getElementById('why-us')?.scrollIntoView({behavior: 'smooth'})} className="bg-transparent border border-slate-600 hover:border-white hover:bg-white hover:text-slate-900 rounded-full px-6 h-11 transition-all">
                    Learn Our Process <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
              <div className="flex-1 min-h-[300px] relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F1C] via-[#0A0F1C]/80 to-transparent z-10 w-full md:w-1/3"></div>
                <OptimizedImage src="/promo.jpg" alt="Supply Chain" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&q=80"; }} />
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Split Section: Industries & Testimonials */}
      <section className="py-8 bg-white" id="why-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FadeInSection>
              <div className="bg-slate-50 rounded-3xl p-8 md:p-12 border border-slate-100 flex flex-col h-full">
                <div className="mb-8">
                  <h3 id="industries" className="text-xl font-bold text-slate-900 mb-2">Industries We Serve</h3>
                  <p className="text-sm text-slate-500">Tailored solutions across every sector.</p>
                </div>
                <div className="flex flex-wrap gap-2 mt-auto">
                  {['Retail', 'Hospitality', 'Events', 'Construction', 'Healthcare', 'Corporate', 'Real Estate', 'Government'].map(ind => (
                    <span key={ind} className="px-3 py-1.5 bg-white border border-slate-200 text-slate-700 text-xs font-bold rounded-lg shadow-sm">
                      {ind}
                    </span>
                  ))}
                </div>
              </div>
            </FadeInSection>

            <FadeInSection delay={100}>
              <div className="bg-slate-50 rounded-3xl p-8 md:p-12 border border-slate-100 flex flex-col h-full">
                <div className="mb-6 flex gap-1 text-yellow-400">
                  <Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-6 italic leading-relaxed">
                  "All In Solutions took the stress out of our entire exhibition print run. Delivered on time, on budget, and the quality was exceptional."
                </h3>
                <div className="mt-auto flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-sm">
                    SM
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-sm">Sarah Mitchell</p>
                    <p className="text-xs text-slate-500">Events Director, Pinnacle Events</p>
                  </div>
                </div>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* Newsletter / CTA */}
      <section className="py-12 bg-white mb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection>
            <div className="bg-blue-600 rounded-3xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between shadow-xl shadow-blue-600/20">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-6 md:mb-0 text-center md:text-left">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm shrink-0">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <div className="pt-1">
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 tracking-tight">Ready to start your project?</h2>
                  <p className="text-blue-100 text-sm md:text-base max-w-md">Get a free consultation and quote for your next print or marketing campaign.</p>
                </div>
              </div>
              <div className="w-full md:w-auto shrink-0">
                <Button onClick={() => openQuote()} size="lg" className="w-full md:w-auto bg-[#0A0F1C] hover:bg-slate-800 text-white rounded-xl h-14 px-8 font-semibold text-base transition-colors border-0">
                  Request Quote
                </Button>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      </main>
      <SiteFooter />
    </div>
  );
}
